import { useEffect, useState } from "react";
import Seo from "@/components/base/Seo";
import AdminPageHeader from "../components/AdminPageHeader";
import { useAuth } from "@/context/AuthContext";
import { getAdminPlans, saveAdminPlan } from "@/services/adminApi";
import type { Plan } from "@/types";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1 block text-[11px] font-semibold uppercase tracking-wide text-foreground-500";

type PlanGroup = NonNullable<Plan["group"]>;
type PlanPeriod = "forever" | "year" | "month";

interface Draft {
  name: string;
  tagline: string;
  icon: string;
  price: string;
  period: PlanPeriod;
  validity: string;
  ctaLabel: string;
  isPopular: boolean;
  group: PlanGroup;
  displayOrder: string;
  chips: string;
  features: string;
}

function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function toDraft(plan: Plan): Draft {
  return {
    name: plan.name,
    tagline: plan.tagline,
    icon: plan.icon || "",
    price: String(plan.price),
    period: (plan.period === "year" || plan.period === "month" ? plan.period : "forever") as PlanPeriod,
    validity: plan.validity || "",
    ctaLabel: plan.ctaLabel || "",
    isPopular: plan.isPopular,
    group: plan.group === "placement" ? "placement" : "subscription",
    displayOrder: String((plan as Plan & { displayOrder?: number }).displayOrder ?? 0),
    chips: (plan.chips || []).join(", "),
    features: plan.features.join("\n"),
  };
}

function toPayload(draft: Draft): Partial<Plan> {
  return {
    name: draft.name.trim(),
    tagline: draft.tagline.trim(),
    icon: draft.icon.trim(),
    price: Math.max(0, Number(draft.price) || 0),
    period: draft.period,
    validity: draft.validity.trim(),
    ctaLabel: draft.ctaLabel.trim(),
    isPopular: draft.isPopular,
    group: draft.group,
    displayOrder: Math.max(0, Math.floor(Number(draft.displayOrder) || 0)),
    chips: splitCsv(draft.chips),
    features: draft.features
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean),
  };
}

interface CardProps {
  plan: Plan;
  token: string | null;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

function PlanEditorCard({ plan, token, onSaved, onError }: CardProps) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(plan));
  const [saving, setSaving] = useState(false);

  // Re-seed the draft if the row is replaced (e.g. after a refetch).
  useEffect(() => {
    setDraft(toDraft(plan));
  }, [plan]);

  const patch = (part: Partial<Draft>) => setDraft((d) => ({ ...d, ...part }));

  const submit = async () => {
    setSaving(true);
    try {
      await saveAdminPlan(plan.id, toPayload(draft), token);
      onSaved(`“${draft.name}” plan saved.`);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not save the plan. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className={`${draft.icon || "ri-price-tag-3-line"} text-lg`}></i>
        </span>
        <div className="min-w-0">
          <h2 className="truncate font-heading text-base font-bold text-foreground-950">
            {draft.name || "Untitled plan"}
          </h2>
          <p className="text-xs text-foreground-500">
            {draft.group === "placement" ? "Short-term placement" : "Recurring subscription"}
            {draft.isPopular ? " · most popular" : ""}
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelClass}>Name</label>
          <input className={inputClass} value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Tagline</label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={2}
            value={draft.tagline}
            onChange={(e) => patch({ tagline: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass}>Price (₹)</label>
          <input
            type="number"
            min={0}
            className={inputClass}
            value={draft.price}
            onChange={(e) => patch({ price: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass}>Billing period</label>
          <select
            className={`${inputClass} cursor-pointer`}
            value={draft.period}
            onChange={(e) => patch({ period: e.target.value as PlanPeriod })}
          >
            <option value="forever">Forever (free)</option>
            <option value="year">Per year</option>
            <option value="month">Per month</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Validity label</label>
          <input
            className={inputClass}
            placeholder="e.g. 12 months"
            value={draft.validity}
            onChange={(e) => patch({ validity: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass}>CTA button label</label>
          <input
            className={inputClass}
            placeholder="e.g. Upgrade to Business"
            value={draft.ctaLabel}
            onChange={(e) => patch({ ctaLabel: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass}>Icon (Remix class)</label>
          <input
            className={inputClass}
            placeholder="ri-rocket-2-line"
            value={draft.icon}
            onChange={(e) => patch({ icon: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass}>Section</label>
          <select
            className={`${inputClass} cursor-pointer`}
            value={draft.group}
            onChange={(e) => patch({ group: e.target.value as PlanGroup })}
          >
            <option value="subscription">Subscription</option>
            <option value="placement">Placement boost</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Display order</label>
          <input
            type="number"
            min={0}
            className={inputClass}
            value={draft.displayOrder}
            onChange={(e) => patch({ displayOrder: e.target.value })}
          />
        </div>
        <div className="flex items-end">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground-800">
            <input
              type="checkbox"
              className="h-4 w-4 cursor-pointer accent-primary-500"
              checked={draft.isPopular}
              onChange={(e) => patch({ isPopular: e.target.checked })}
            />
            Mark as “Most popular”
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>What you can add (comma separated)</label>
          <input
            className={inputClass}
            placeholder="1 category, 7 photos"
            value={draft.chips}
            onChange={(e) => patch({ chips: e.target.value })}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>Features (one per line)</label>
          <textarea
            className={`${inputClass} resize-y`}
            rows={5}
            value={draft.features}
            onChange={(e) => patch({ features: e.target.value })}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
        >
          <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"}></i>
          {saving ? "Saving…" : "Save plan"}
        </button>
      </div>
    </article>
  );
}

export default function AdminPlans() {
  const { token } = useAuth();
  const [rows, setRows] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setLoadError(false);
    getAdminPlans(token)
      .then((p) => setRows(p))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const showNotice = (message: string) => {
    setError("");
    setNotice(message);
    setTimeout(() => setNotice(""), 2600);
  };

  return (
    <>
      <Seo title="Listing Plans" path="/admin/plans" />

      <AdminPageHeader
        icon="ri-price-tag-3-line"
        title="Listing plans"
        subtitle="Edit the plans, pricing and features shown on the public listing-plans page."
        actions={
          notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      {error && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
          <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
          ))}
        </div>
      ) : loadError ? (
        <div className="mt-6 rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
          <p>Couldn&apos;t load the listing plans.</p>
          <button
            type="button"
            onClick={load}
            className="mt-3 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-foreground-950 hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line"></i> Try again
          </button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {rows.map((plan) => (
            <PlanEditorCard
              key={plan.id}
              plan={plan}
              token={token}
              onSaved={showNotice}
              onError={(msg) => {
                setNotice("");
                setError(msg);
              }}
            />
          ))}
        </div>
      )}

      <p className="mt-6 text-xs text-foreground-500">
        Changes appear on the public <span className="font-medium text-foreground-700">/listing-plans</span> page
        immediately — no rebuild required.
      </p>
    </>
  );
}